import { gql, useQuery } from '@apollo/client'
import { useEffect, useState } from 'react'

import { currentArtistVar } from '@/clients/utoniq-core'
import {
  ArtistBottomTabFragmentDoc,
  ArtistSearchQuery,
  ArtistSearchQueryVariables,
  CurrentUserFragmentDoc,
  MonthlyEventsByArtistFragmentDoc,
  MonthlyEventsByArtistQuery,
  MonthlyEventsByArtistQueryVariables,
} from '@/clients/utoniq-core/schema'
import { config, constants } from '@/config'
import { useRouter } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { useCurrentUser } from '@/core/auth'
import { dayjs } from '@/lib/dayjs'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const [month, setMonth] = useState(dayjs().format('YYYYMM'))
  const { userClaims } = useCurrentUser()
  const { router } = useRouter<{ artistId: string }>()
  const artistId = router.query.artistId ?? config.artistId

  const { data, loading } = useQuery<
    MonthlyEventsByArtistQuery,
    MonthlyEventsByArtistQueryVariables
  >(QUERY, {
    variables: { artistId, month, id: artistId },
    onCompleted(data) {
      if (data?.artist) {
        currentArtistVar(data.artist)
      }
    },
  })

  const { data: userData } = useQuery<
    ArtistSearchQuery,
    ArtistSearchQueryVariables
  >(USER_QUERY)

  useEffect(() => {
    const month = sessionStorage.getItem(constants.storageKeys.EVENT_MONTH)
    if (month) {
      setMonth(month)
      sessionStorage.removeItem(constants.storageKeys.EVENT_MONTH)
    }
  }, [])

  return {
    state: {
      loading: loading,
      month,
      user: userData?.user || null,
      isArtist: !!userClaims?.orgs?.[config.organizationId]?.artist,
      followArtists: userData?.user?.organizationFollowees ?? null,
      memberArtists: userData?.user?.organizationPatrons ?? null,
      liveStreamId: data?.monthlyEventsByArtist?.length
        ? data.monthlyEventsByArtist[0].artist?.currentLive?.id
        : undefined,
      monthlyEventsByArtist: data?.monthlyEventsByArtist ?? null,
    },
    actions: {
      async onFetch(month: string) {
        setMonth(month)
      },
    },
  }
})

const QUERY = gql`
  query MonthlyEventsByArtist($id: ID, $artistId: String!, $month: String!) {
    artist(id: $id) {
      ...ArtistBottomTab
      id
      currentLive {
        id
      }
    }
    monthlyEventsByArtist(month: $month, artistId: $artistId) {
      ...MonthlyEventsByArtist
    }
  }
  ${ArtistBottomTabFragmentDoc}
  ${MonthlyEventsByArtistFragmentDoc}
`

const USER_QUERY = gql`
  query ArtistSearch {
    user {
      ...CurrentUser
    }
  }
  ${CurrentUserFragmentDoc}
`
